import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchWarehouseStatusLineNonSeedThunk } from "../../store/reports/thunk";
import "../../styles/global.css";
import { setDate } from "../../store/reports/actions";
import {
  Home,
  PauseCircle,
  PlayCircle,
  ChevronLeftCircle,
  ChevronRightCircle,
} from "lucide-react";
import { fetchWarehouseThunk } from "../../store/redux/thunk";
import { APP_CONSTANTS } from "../../utilities/app_constants";

const StockPositionNonSeed = ({
  reportData,
  onNext,
  onPrevious,
  onPause,
  isPaused,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useState({});
  const { nonSeedLines, currentDate, loading, error } = useSelector(
    (state) => state.report
  );
  const { warehouse } = useSelector((state) => state.filter);

  // Extract parameters from the URL
  useEffect(() => {
    if (reportData?.url) {
      const urlParams = new URLSearchParams(reportData.url.split("?")[1]);
      const extractedParams = {
        warehouseId: urlParams.get("M_Warehouse_ID"),
        note: urlParams.get("Note"),
      };
      setParams(extractedParams);
    }
  }, [reportData]);

  // Fetch data
  useEffect(() => {
    if (params.warehouseId) {
      dispatch(fetchWarehouseThunk(params.warehouseId));
      dispatch(setDate(new Date().toISOString().split("T")[0]));
      dispatch(fetchWarehouseStatusLineNonSeedThunk(params.warehouseId));
    }
  }, [dispatch, params]);

  // Filter data with null/undefined check
  const packingMaterials = nonSeedLines && nonSeedLines.length > 0
    ? nonSeedLines.filter((item) =>
        /pack|bag|box|material/i.test(item.product)
      )
    : [];

  const chemicalProducts = nonSeedLines && nonSeedLines.length > 0
    ? nonSeedLines.filter((item) =>
        /chemical/i.test(item.product)
      )
    : [];
  
  // Auto-Navigate after interval if not paused
  useEffect(() => {
    if (!isPaused) {
      const intervalInSeconds = parseInt(reportData?.interval, 10);
      const displayTime =
        !isNaN(intervalInSeconds) && intervalInSeconds > 0
          ? intervalInSeconds * 1000
          : APP_CONSTANTS.REPORT_DISPLAY_TIME;

      const timeout = setTimeout(() => {
        onNext();
      }, displayTime);

      return () => clearTimeout(timeout);
    }
  }, [onNext, isPaused, reportData?.interval]);

  // Add responsive layout styles WITH sticky header implementation
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      /* ONLY RESPONSIVE LAYOUT CHANGES - NO UI/COLOR CHANGES */
      
      /* Container responsiveness */
      .container {
        max-width: 100%;
        overflow-x: hidden;
      }
      
      /* Header responsive layout */
      @media (max-width: 1024px) {
        .header {
          flex-wrap: wrap !important;
          justify-content: center !important;
        }
        
        .header .home-button {
          order: 1;
        }
        
        .header .controls {
          order: 2;
        }
        
        .header h1 {
          order: 3;
          width: 100%;
          text-align: center;
          margin: 10px 0 !important;
        }
        
        .header .header-info {
          order: 4;
          width: 100%;
          justify-content: center !important;
          margin: 8px 0 !important;
        }
        
        .header .date-display {
          order: 5;
          margin: 8px auto !important;
        }
        
        .header .logo-container {
          order: 6;
          margin: 5px 0 !important;
        }
      }
      
      @media (max-width: 768px) {
        .header .controls svg {
          width: 32px !important;
          height: 32px !important;
        }
        
        .header h1 {
          font-size: 18px !important;
        }
        
        .current-date {
          width: 70px !important;
          font-size: 11px !important;
        }
      }
      
      @media (max-width: 480px) {
        .header .controls {
          gap: 5px !important;
        }
        
        .header .controls svg {
          width: 28px !important;
          height: 28px !important;
        }
        
        .header h1 {
          font-size: 16px !important;
        }
        
        .current-date {
          width: 60px !important;
          font-size: 10px !important;
        }
      }
      
      /* Tables row responsive - stacks on mobile */
      @media (max-width: 768px) {
        .nonseed-tables-row {
          flex-direction: column !important;
          gap: 15px !important;
          margin: 15px !important;
        }
        
        .nonseed-table-wrapper {
          width: 100% !important;
          margin-bottom: 0 !important;
        }
      }
      
      /* Table responsiveness - horizontal scroll */
      .nonseed-table-container {
        overflow-x: auto;
        width: 100%;
        max-height: 500px;
        overflow-y: auto;
        position: relative;
      }
      
      .nonseed-data-table {
        min-width: 500px;
      }
      
      /* Responsive table cell styles */
      @media (max-width: 768px) {
        .nonseed-data-table th,
        .nonseed-data-table td {
          padding: 8px !important;
          font-size: 11px !important;
        }
        
        .nonseed-section-title {
          font-size: 16px !important;
          padding: 10px !important;
        }
        
        .product-cell {
          font-size: 11px !important;
        }
      }
      
      @media (max-width: 480px) {
        .nonseed-data-table th,
        .nonseed-data-table td {
          padding: 6px !important;
          font-size: 9px !important;
        }
        
        .nonseed-section-title {
          font-size: 14px !important;
          padding: 8px !important;
        }
      }
      
      /* Marquee responsiveness */
      @media (max-width: 768px) {
        .marquee-container {
          font-size: 14px !important;
          bottom: 20px !important;
        }
        
        .marquee-container div span {
          padding-right: 60px !important;
        }
      }
      
      @media (max-width: 480px) {
        .marquee-container {
          font-size: 11px !important;
          bottom: 10px !important;
          padding: 8px 0 !important;
        }
        
        .marquee-container div span {
          padding-right: 40px !important;
        }
      }
      
      /* Keep ALL original table styles exactly as they were */
      .nonseed-tables-row {
        display: flex;
        gap: 20px;
        margin: 20px;
        flex-wrap: wrap;
      }
      
      .nonseed-table-wrapper {
        flex: 1;
        min-width: 300px;
        border: 1px solid #ddd;
        border-radius: 8px;
        overflow: hidden;
        background: white;
        position: relative;
      }
      
      .nonseed-table-container {
        overflow-x: auto;
        overflow-y: auto;
        max-height: 80vh;
        width: 100%;
        position: relative;
      }
      
      .nonseed-data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }
      
      .nonseed-data-table th,
      .nonseed-data-table td {
        border: 1px solid #ddd;
        padding: 12px;
        text-align: left;
        vertical-align: middle;
      }
      
      .nonseed-data-table th {
        background-color: #0070C0;
        color: white;
        font-weight: bold;
        text-align: center;
      }
      
      /* ===== STICKY HEADER IMPLEMENTATION ===== */
      /* Make table headers sticky */
      .nonseed-table-container .nonseed-data-table thead th {
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: #0070C0 !important;
      }
      
      /* Add box shadow for better visual separation when scrolling */
      .nonseed-table-container .nonseed-data-table thead th {
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      /* Ensure thead background doesn't show through */
      .nonseed-table-container .nonseed-data-table thead {
        position: sticky;
        top: 0;
        z-index: 10;
      }
      
      .nonseed-section-title {
        background: linear-gradient(135deg, #0A2A88 0%, #1565C0 100%);
        color: white;
        padding: 12px 20px;
        margin: 0;
        font-size: 18px;
        font-weight: bold;
        text-align: center;
        position: sticky;
        top: 0;
        z-index: 15;
      }
      
      .loading, .error {
        text-align: center;
        padding: 40px;
        font-size: 18px;
      }
      
      .no-data {
        text-align: center;
        padding: 40px;
      }
      
      .product-cell {
        font-weight: bold;
        background-color: #fafafa;
        text-align: center;
        vertical-align: middle;
      }
      
      .variety-cell {
        word-break: break-word;
        white-space: normal;
      }
      
      .quantity-cell, .uom-cell {
        text-align: center;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const renderTable = (title, data) => {
    // Check if data exists and has length
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    // Get unique products safely
    const uniqueProducts = [...new Set(data.map((item) => item.product))];
    
    if (uniqueProducts.length === 0) return null;

    return (
      <div className="nonseed-table-wrapper" key={title}>
        <div className="nonseed-section-title">{title}</div>
        <div className="nonseed-table-container">
          <table className="nonseed-data-table">
            <thead>
              <tr>
                <th style={{ minWidth: "120px", position: "sticky", top: 0, backgroundColor: "#0070C0", zIndex: 10 }}>Product</th>
                <th style={{ minWidth: "180px", position: "sticky", top: 0, backgroundColor: "#0070C0", zIndex: 10 }}>Variety</th>
                <th style={{ minWidth: "100px", position: "sticky", top: 0, backgroundColor: "#0070C0", zIndex: 10 }}>Quantity</th>
                <th style={{ minWidth: "80px", position: "sticky", top: 0, backgroundColor: "#0070C0", zIndex: 10 }}>UOM</th>
              </tr>
            </thead>
            <tbody>
              {uniqueProducts.map((product, rowIndex) => {
                const productEntries = data.filter(
                  (item) => item.product === product
                );
                return productEntries.map((entry, entryIndex) => (
                  <tr key={`${product}-${entryIndex}`}>
                    {entryIndex === 0 && (
                      <td
                        rowSpan={productEntries.length}
                        className="product-cell"
                      >
                        {product || "-"}
                      </td>
                    )}
                    <td className="variety-cell">
                      {entry.fieldvariety || "-"}
                    </td>
                    <td className="quantity-cell">
                      {entry.pendingQty || "-"}
                    </td>
                    <td className="uom-cell">
                      {entry.quantityUom || "-"}
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Scrolling text content
  const scrollingText = `${params?.note || ""}`;

  return (
    <>
      <div className="container" style={{ position: "relative", minHeight: "100vh" }}>
        <header
          className="header"
          style={{
            flexWrap: "wrap",
            gap: "1px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <button
            className="home-button"
            onClick={() =>
              navigate("/menu", {
                state: { hideModal: true },
              })
            }
          >
            <Home
              className="home-icon"
              size={120}
              style={{
                width: "35px",
                height: "40px",
              }}
            />
          </button>
          
          <div
            className="controls"
            style={{ display: "flex", gap: "1px", alignItems: "center" }}
          >
            <ChevronLeftCircle
              size={40}
              onClick={onPrevious}
              style={{ cursor: "pointer" }}
            />
            {isPaused ? (
              <PlayCircle
                size={40}
                onClick={() => onPause(false)}
                style={{ cursor: "pointer" }}
              />
            ) : (
              <PauseCircle
                size={40}
                onClick={() => onPause(true)}
                style={{ cursor: "pointer" }}
              />
            )}
            <ChevronRightCircle
              size={40}
              onClick={onNext}
              style={{ cursor: "pointer" }}
            />
          </div>
          
          <h1 style={{ fontSize: "clamp(16px, 4vw, 28px)", margin: "5px 0" }}>
            Stock Position Non-Seed
          </h1>
          
          <div className="header-info">
            <span
              style={{
                color: "#FFEB3B",
                fontSize: "clamp(16px, 3vw, 28px)",
                fontWeight: "bold",
              }}
            >
              <span
                style={{
                  background: "linear-gradient(90deg, #0A2A88, #1565C0)",
                  color: "#FFFFFF",
                  padding: "6px 9px",
                  borderRadius: "30px",
                  fontSize: "clamp(14px, 2vw, 28px)",
                  fontWeight: "800",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  border: "2px solid rgba(255,255,255,0.7)",
                  letterSpacing: "0.5px",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: "#BBDEFB",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    opacity: 0.9,
                  }}
                >
                  Warehouse
                </span>

                <span
                  style={{
                    width: "2px",
                    height: "22px",
                    background: "rgba(255,255,255,0.5)",
                  }}
                />

                <span
                  style={{
                    color: "#FFFFFF",
                    wordBreak: "break-word",
                    maxWidth: "400px",
                    fontWeight: "900",
                    whiteSpace: "normal",
                    textShadow: "0 0 8px black, 0 0 12px black",
                  }}
                >
                  {warehouse?.data?.length > 0
                    ? warehouse.data[0].name
                    : "-"}
                </span>
              </span>
            </span>
          </div>
          
          <div
            className="date-display"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <span>Current Date:</span>
            <input
              type="text"
              value={currentDate || ""}
              readOnly
              className="current-date"
              style={{
                width: "80px",
                minWidth: "80px",
                padding: "4px 8px",
                fontSize: "14px",
              }}
            />
          </div>
          
          <div className="logo-container">
            <a
              href="https://www.digiopenservices.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/Logo.png"
                alt="Logo"
                className="header-logo"
                style={{
                  maxWidth: "110px",
                  height: "40px",
                  objectFit: "contain",
                  borderRadius: "6px",
                  margin: "2px 10px",
                  cursor: "pointer",
                }}
              />
            </a>
          </div>
        </header>

        {/* {loading && <p className="loading">Loading data, please wait...</p>} */}
        {error && <p className="error">{error}</p>}
        
        {!loading && (!nonSeedLines || nonSeedLines.length === 0) && (
          <div className="no-data">
            <p>No stock position data available.</p>
          </div>
        )}

        {/* Both Tables in Same Row */}
        {(packingMaterials.length > 0 || chemicalProducts.length > 0) && (
          <div className="nonseed-tables-row">
            {/* Packing Materials Table */}
            {renderTable("Packing Materials", packingMaterials)}
            
            {/* Chemical Products Table */}
            {renderTable("Chemical Materials", chemicalProducts)}
          </div>
        )}
      </div>

       <div
        style={{
          position: "fixed",
          bottom: "40px",
          left: 0,
          right: 0,
          color: "#022fe3",
          padding: "12px 0",
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          fontSize: "25px",
          fontWeight: "700",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: "inline-block",
            animation: "scrollRightToLeft 35s linear infinite",
            whiteSpace: "nowrap",
            paddingLeft: "100%",
          }}
        >
          <span>{scrollingText}</span>
        </div>

        <style>
          {`
      @keyframes scrollRightToLeft {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(-100%);
        }
      }
    `}
        </style>
      </div>
    </>
  );
};

export default StockPositionNonSeed;